import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { Alert, AlertService, AlertType, ModalComponent, ModalResult, TableComponent, TableConfig } from '@michelin/theme';
import { DisposalCharge } from '../../models/response.model';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { MatTableDataSource } from '@angular/material/table';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';

@Component({
  selector: 'app-disposal-charges',
  templateUrl: './disposal-charges.component.html',
  styleUrl: './disposal-charges.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisposalChargesComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<DisposalCharge>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureflagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);

  disposalCharges = new MatTableDataSource();
  rmPartNums = [];
  fgPartNums = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;
  isLoading = true;

  tableConfig: TableConfig<DisposalCharge> = {
    columns: {
      code: {
        cell: (item: DisposalCharge) => item.code,
        header: this.translationService.translate('codeText')
      },
      description: {
        cell: (item: DisposalCharge) => item.description,
        header: this.translationService.translate('descriptionText')
      },
      rmPartNum: {
        cell: 'rmPartNum',
        header: 'RM #'
      },
      fgPartNum: {
        cell: 'fgPartNum',
        header: 'FG #'
      },
      defaultDisposalCharges: {
        cell: 'default',
        header: this.translationService.translate('defaultText')
      }
    }
  };

  ngOnInit(): void {
    this.isLoading = true;
    this.productSaveDisabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.rmPartNums = this.store.selectSnapshot(ProductsState.products).rollPartNumbers;
    this.fgPartNums = this.store.selectSnapshot(ProductsState.products).finishedPartNumbers;
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.displayedRowsForm.dirty;
    });
    this.isLoading = true;
    this.productDataService.getDisposalCharges().subscribe({
      next: (response) => {
        this.disposalCharges.data = response.data;
        this.updateDisplayedRowsForm(this.disposalCharges.data as DisposalCharge[]);
        this.displayedRowsForm.markAsPristine();
        this.isModified = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
        this.cdr.markForCheck();
      }
    });
  }

  createRowForm(row: DisposalCharge): FormGroup {
    return this.fb.group({
      id: [row.id],
      code: [row.code],
      description: [row.description],
      rmPartNum: [row.rmPartNum],
      fgPartNum: [row.fgPartNum],
      defaultDisposalCharges: [row.defaultDisposalCharges === 'Y']
    });
  }

  private updateDisplayedRowsForm(rows: DisposalCharge[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: DisposalCharge, controlName: keyof DisposalCharge): FormControl {
    let disposalC = this.disposalCharges.data as DisposalCharge[];
    const index = disposalC.findIndex((row: DisposalCharge) => row.id === item.id);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = [];
    let disposalC = this.disposalCharges.data as DisposalCharge[];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;

    const checkedCount = formArray.controls.filter((control) => control.get('defaultDisposalCharges')?.value === true).length;

    if (checkedCount != 1) {
      if (checkedCount === 0) {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('dispNoDefaultText')
        } as Alert);
      } else {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('dispMultiDefaultText')
        } as Alert);
      }
      return;
    }

    for (let i = 0; i < disposalC.length; i++) {
      const original = disposalC[i];
      const current = formArray.at(i).value;
      // Normalize boolean to 'Y'/'N' for comparison
      const currentDefaultAsString = current.defaultDisposalCharges === true ? 'Y' : 'N';
      // Check if modified by comparing the normalized value
      const isModified = Object.keys(current).some((key) => {
        if (key === 'defaultDisposalCharges') {
          return currentDefaultAsString !== (original[key] ?? 'N');
        }
        return current[key] !== original[key];
      });
      if (isModified) {
        current.defaultDisposalCharges = currentDefaultAsString;
        modifiedRows.push({ ...current, id: original.id });
      }
    }
    if (modifiedRows.length > 0) {
      this.productDataService.updateDisposalCharges(modifiedRows).subscribe(() => {
        this.disposalCharges.data = disposalC.map((original) => {
          const saved = modifiedRows.find((row) => row.id === original.id);
          return saved ? { ...original, ...saved } : original;
        });
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
        this.displayedRowsForm.markAsPristine();
        this.isModified = false;
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.disposalCharges.data as DisposalCharge[]);
        if (this.tableRef) {
          this.tableRef.resetFilters();
          this.tableRef.patchExtraFilters({}, true);
        }
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.isModified && this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  checkIfModified(): boolean {
    return this.isModified;
  }
}
